import { useEffect, useRef, useState } from "react"

// Shows a count chip only after `count` has stayed non-zero for the
// debounce window. Drops to hidden immediately when the count returns to
// zero, so transient states (e.g. sub-second `running` rows from
// idempotent enrichment short-circuits) never blip onto the canvas.
//
// Currently used for the running-executions chip only — long-running steps
// (5-30s LLM calls) cross the threshold and surface as expected; short ones
// resolve before the timer fires and stay invisible.
const DEBOUNCE_MS = 2000

export default function DebouncedCountChip({ count, label, tone }) {
  const displayed = useDebouncedValue(count, DEBOUNCE_MS)
  if (!displayed) return null

  return (
    <span className={tone}>
      <span className="font-semibold">{displayed}</span>{" "}
      <span className="uppercase tracking-wider opacity-80">{label}</span>
    </span>
  )
}

function useDebouncedValue(value, delayMs) {
  const [displayed, setDisplayed] = useState(0)
  const timer = useRef(null)

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    if (!value) {
      setDisplayed(0)
      return undefined
    }
    timer.current = setTimeout(() => {
      setDisplayed(value)
      timer.current = null
    }, delayMs)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [value, delayMs])

  return displayed
}
